import { Box, Typography, useTheme } from "@mui/material";

import { WidgetHeader } from "components/atoms/WidgetHeader";
import { WidgetPaper } from "components/atoms/WidgetPaper";
import { useDDFilter } from "components/modules/disinfectionFilter/DisinfectionFilterPanel";
import { format, isAfter, isSameDay, isWeekend, startOfWeek } from "date-fns";
import { graphql } from "gql";
import { DisinfectionFilter, GroupBy } from "gql/graphql";
import { useMemo, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useQueryWithSnack } from "utils/useQueryWithSnack";
import { utcDateToLocaleDate } from "utils/utilFunctions";
import { DualNumberChartColumn } from "../../modules/ChartColumns";

const ITEMS_PER_PAGE = 31;

interface Props {
  color?: string;
  hiddenFilter?: DisinfectionFilter;
  groupBy?: GroupBy;
}

export const CYCLES_COUNTER_QUERY = graphql(`
  query CyclesCounter(
    $filter: DisinfectionFilter!
    $groupBy: GroupBy!
    $timezone: String!
  ) {
    numberOfDisinfectionCycles(
      filter: $filter
      groupBy: $groupBy
      timezone: $timezone
    ) {
      date
      count
      complete
      incomplete
      interruptions
      disinfectionsWithInterruptions
    }
  }
`);

export function getDaysFromSpan(start: Date, end: Date) {
  const dateIterator = new Date(start);
  dateIterator.setHours(0, 0, 0, 0);

  const endDate = new Date(end);
  endDate.setHours(23, 59, 59, 999);

  const days: Date[] = [];
  while (dateIterator <= endDate) {
    days.push(new Date(dateIterator));
    dateIterator.setDate(dateIterator.getDate() + 1);
  }
  return days;
}

interface Props {
  color?: string;
  hiddenFilter?: DisinfectionFilter;
  groupBy?: GroupBy;
}

export const CycleOverviewChart = ({
  color,
  hiddenFilter,
  groupBy = GroupBy.Day,
}: Props) => {
  void color;
  const [filterParams] = useDDFilter();
  const containerRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  const { data } = useQueryWithSnack(CYCLES_COUNTER_QUERY, {
    variables: {
      filter: { ...filterParams, ...hiddenFilter },
      groupBy,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  });

  const getDisplayDate = (date: Date) => {
    switch (groupBy) {
      case GroupBy.Month:
        return format(date, "MMM yyyy");
      case GroupBy.Week:
        return `Week ${format(date, "w")}`;
      case GroupBy.Day:
      default:
        return date.getDate();
    }
  };

  const cyclesByPeriod = useMemo(() => {
    const currentPeriodDates = filterParams
      ? getDaysFromSpan(filterParams.timeSpan.start, filterParams.timeSpan.end)
      : null;

    const largestNumberOfCycles = Math.max(
      ...(data?.numberOfDisinfectionCycles ?? []).map((item) => item.count),
    );

    const groupDates = (dates: Date[]) => {
      if (!dates.length) return [];

      switch (groupBy) {
        case GroupBy.Month: {
          const groups = new Map();
          dates.forEach((date) => {
            const key = `${date.getFullYear()}-${date.getMonth()}`;
            const existingDate = groups.get(key);
            // Keep first day of each month
            if (!existingDate) {
              const firstDayOfMonth = new Date(
                date.getFullYear(),
                date.getMonth(),
                1,
              );
              groups.set(key, firstDayOfMonth);
            }
          });
          return Array.from(groups.values()).sort(
            (a, b) => a.getTime() - b.getTime(),
          );
        }
        case GroupBy.Week: {
          const groups = new Map();
          dates.forEach((date) => {
            const weekStart = startOfWeek(date, { weekStartsOn: 1 });
            const key = weekStart.getTime();
            if (!groups.has(key)) {
              groups.set(key, weekStart);
            }
          });
          return Array.from(groups.values()).sort(
            (a, b) => a.getTime() - b.getTime(),
          );
        }
        case GroupBy.Day:
        default:
          return dates;
      }
    };

    const groupedDates = currentPeriodDates
      ? groupDates(currentPeriodDates)
      : [];

    return groupedDates.map((date) => {
      const dataPoint = data?.numberOfDisinfectionCycles.find((item) => {
        const itemDate = utcDateToLocaleDate(new Date(item.date));
        if (groupBy === GroupBy.Month) {
          return (
            itemDate.getMonth() === date.getMonth() &&
            itemDate.getFullYear() === date.getFullYear()
          );
        }
        return isSameDay(itemDate, date);
      });

      return {
        date,
        day: getDisplayDate(date),
        month: date.getMonth(),
        year: date.getFullYear(),
        percentage: ((dataPoint?.count ?? 0) / largestNumberOfCycles) * 100,
        count: dataPoint?.count ?? 0,
        complete: dataPoint?.complete ?? 0,
        incomplete: dataPoint?.incomplete ?? 0,
        interruptions: dataPoint?.interruptions ?? 0,
        isWeekend: groupBy === GroupBy.Day ? isWeekend(date) : false,
        hide: isAfter(date, new Date()),
      };
    });
  }, [data, filterParams.timeSpan, groupBy]);

  const averageCyclesPerDay = useMemo(() => {
    const days = cyclesByPeriod?.filter((item) => !item.hide);
    if (!days?.length) return 0;

    const average =
      Math.round(
        (days.reduce((sum, item) => sum + item.count, 0) / days.length) * 10,
      ) / 10;

    return average > 0 && average < 1 ? "Less than 1" : Math.round(average);
  }, [cyclesByPeriod]);

  const header = filterParams.timeSpan
    ? `${format(filterParams.timeSpan.start, "dd MMM yyyy")} - ${format(
        filterParams.timeSpan.end,
        "dd MMM yyyy",
      )}`
    : "-";

  // Starting value should exceed ITEMS_PER_PAGE, scrollbar does not spawn without overflow
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE * 3);

  const handleScroll = () => {
    const container = containerRef.current;
    if (!container) return;

    const scrollLeft = container.scrollLeft;
    const containerWidth = container.clientWidth;
    const scrollWidth = container.scrollWidth;

    // If we're past 50% of scrollable content, buffer some more data
    if (scrollLeft > (scrollWidth - containerWidth) * 0.5) {
      setVisibleCount((prev) =>
        Math.min(prev + ITEMS_PER_PAGE, cyclesByPeriod?.length ?? 0),
      );
    }
  };

  return (
    <WidgetPaper
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "top",
          marginBottom: theme.spacing(1),
        }}
      >
        <Box sx={{ maxWidth: "40%" }}>
          <WidgetHeader
            label={header}
            description={
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Box sx={{ display: "flex", flexDirection: "row" }}>
                  <FormattedMessage
                    id="cycles-count-description-1"
                    values={{
                      incomplete: (
                        <span
                          style={{
                            color: theme.palette.error.main,
                            width: "fit-content",
                            display: "inline-block",
                            marginLeft: "2px",
                          }}
                        >
                          incomplete
                        </span>
                      ),
                      complete: (
                        <span
                          style={{
                            color: theme.palette.success.main,
                            width: "fit-content",
                            display: "inline-block",
                          }}
                        >
                          complete
                        </span>
                      ),
                      total: (
                        <span
                          style={{
                            color: "black",
                            width: "fit-content",
                            display: "inline-block",
                          }}
                        >
                          total
                        </span>
                      ),
                    }}
                    defaultMessage="Number of {incomplete}/{complete}/{total}"
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    marginBottom: "5px",
                  }}
                >
                  <FormattedMessage
                    id="cycles-count-description-2"
                    values={{
                      period: GroupBy[groupBy].toLowerCase(),
                    }}
                    defaultMessage="disinfection cycles per {period}"
                  />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "row" }}>
                  <FormattedMessage
                    id="cycles-count-description-4"
                    values={{
                      interruptions: (
                        <span
                          style={{
                            color: theme.palette.warning.main,
                            width: "fit-content",
                            display: "inline-block",
                            marginLeft: "2px",
                          }}
                        >
                          interruptions
                        </span>
                      ),
                    }}
                    defaultMessage="Also lists the count of {interruptions}"
                  />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "row" }}>
                  <FormattedMessage
                    id="cycles-count-description-4"
                    defaultMessage="which occured across the disinfections"
                  />
                </Box>
              </Box>
            }
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "end",
          }}
        >
          <Typography
            sx={(theme) => ({
              fontSize: "72px",
              lineHeight: "72px",
              fontWeight: 700,
              fontFamily: theme.typography.fontFamily,
              top: "-22px",
            })}
          >
            {averageCyclesPerDay}
          </Typography>
          <Typography
            sx={(theme) => ({
              fontSize: 24,
              color: theme.palette.grey[400],
              marginLeft: theme.spacing(1),
            })}
          >
            disinfections per {`${GroupBy[groupBy].toLowerCase()} (average)`}
          </Typography>
        </Box>
      </Box>

      <Box
        ref={containerRef}
        sx={{
          display: "flex",
          justifyContent:
            cyclesByPeriod && cyclesByPeriod?.length < 31
              ? "flex-start"
              : "space-between",
          marginTop: 4,
          maxWidth: "100%",
          overflow: "auto",
        }}
        onScroll={handleScroll}
      >
        {cyclesByPeriod?.slice(0, visibleCount)?.map((item) => {
          return (
            <DualNumberChartColumn
              key={`${item.date.getTime()}-complete`}
              {...item}
              upperColor="success.main"
              lowerColor="error.main"
              groupBy={groupBy}
              totalCount={item.count}
              upperCount={item.incomplete}
              lowerCount={item.complete}
              percentage={item.percentage}
              hide={item.hide}
              isWeekend={item.isWeekend}
              headerColor={"warning.main"}
              header={
                item.interruptions ? item.interruptions.toString() : undefined
              }
            />
          );
        })}
      </Box>
    </WidgetPaper>
  );
};
